// CompraConcluida.js — Rota /compra-concluida. Tela de retorno do pagamento.
//
// Chega aqui de duas formas:
//  1) De volta da InfinitePay, por URL simples (?pedido=123) — sem estado no
//     navegador. Nesse caso consultamos o status no backend, que reconfere com a
//     InfinitePay se o webhook ainda não tiver chegado.
//  2) Direto do checkout com estado (caso do PIX da Insigne, usado em pedidos
//     abaixo do mínimo da InfinitePay): mostramos o QR Code e o copia-e-cola.
import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { message } from "../../utils/toast";
import SliderProdutos from "../../produto/sliderProduto/SliderProduto";
import TrilhaCompra, { ETAPAS_COMPRA } from "../TrilhaCompra";
import SeloSucesso from "../../ui/SeloSucesso";

import { fmtBRL, fmtPreco, autorDe, capaUrl, previsaoEntrega } from "../../compras/comprasUtils";

const main = process.env.REACT_APP_MAIN;
const API_URL = process.env.REACT_APP_API;

const CompraConcluida = () => {
  const { state, search } = useLocation();
  const navigate = useNavigate();

  const pedidoIdUrl = new URLSearchParams(search).get("pedido");
  const [pedido, setPedido] = useState(null);
  const [carregando, setCarregando] = useState(!!pedidoIdUrl && !state?.order);
  const [erro, setErro] = useState("");

  // "Minhas Compras" vive dentro do Perfil (aba meus-pedidos).
  const irParaMinhasCompras = () => {
    navigate(main + "perfil");
    setTimeout(() => window.dispatchEvent(new CustomEvent("changePerfiltab", { detail: "meus-pedidos" })), 150);
  };

  const consultar = useCallback(async () => {
    if (!pedidoIdUrl) return;
    setCarregando(true);
    setErro("");
    try {
      const resp = await fetch(`${API_URL}pedidos/${pedidoIdUrl}/status-pagamento`);
      const dados = await resp.json();
      if (!resp.ok) throw new Error(dados?.error || "Não foi possível consultar o pedido.");
      setPedido(dados);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, [pedidoIdUrl]);

  useEffect(() => {
    if (pedidoIdUrl && !state?.order) consultar();
  }, [pedidoIdUrl, state, consultar]);

  // ===== Caminho 1: voltou da InfinitePay =====
  if (pedidoIdUrl && !state?.order) {
    if (carregando) {
      return (
        <div className="bg-insigne-bg min-h-screen pt-[var(--nav-h)] px-4 text-white">
          {/* Carregando, erro e "nenhuma compra" são TRÊS TELAS DE ESTADO, e as
              três moravam soltas no meio de uma página vazia — texto centrado
              sem nada em volta parece página quebrada, não resposta. O `.bloco`
              estreito dá lugar à mensagem: a borda diz onde a informação
              começa e onde termina. */}
          <div className="bloco mx-auto my-12 max-w-[440px] p-8 text-center">
            <div className="w-10 h-10 mx-auto rounded-full border-4 border-line border-t-insigne-orange animate-spin mb-4" />
            <p className="text-white/70">Confirmando seu pagamento...</p>
          </div>
        </div>
      );
    }

    if (erro || !pedido) {
      return (
        <div className="bg-insigne-bg min-h-screen pt-[var(--nav-h)] px-4 text-white">
          <div className="bloco mx-auto my-12 max-w-[440px] p-8 text-center">
            <i className="bi bi-exclamation-circle text-5xl text-amber-300 mb-4 block" />
            <h2 className="titulo-display text-[2rem]">Não conseguimos consultar o pedido</h2>
            <p className="text-white/60 text-sm mt-2">{erro}</p>
            <button onClick={irParaMinhasCompras} className="mt-5 rounded-controle px-5 py-2.5 text-sm font-semibold bg-insigne-orange text-paper hover:bg-insigne-orange-dark">
              Ver minhas compras
            </button>
          </div>
        </div>
      );
    }

    const pago = pedido.pago;

    // O pedido tem algum impresso que carrega o e-book de presente?
    //
    // A flag viaja no item desde o `PrecoCard` — o único lugar do site que
    // vende impresso, e portanto o único que sabe, na hora de adicionar ao
    // carrinho, se aquele título carrega o presente. Quando o pedido vem do
    // servidor (retorno pela URL, sem `state`), a flag não vem junto e o
    // aviso simplesmente não aparece: deixar de anunciar um presente que
    // existe custa uma visita a Meus Livros; anunciar um que não existe
    // custa um ticket de suporte.
    const temBrinde = (pedido.livros || []).some(
      (i) => i?.formato === "fisico" && i?.ebook_de_brinde
    );

    return (
      <div className="bg-insigne-bg fundo-abertura min-h-screen pt-[var(--nav-h)] px-4 md:px-8 pb-16 text-white">
        <div className="max-w-[1180px] mx-auto">
          {/* A trilha ficava dentro de uma caixa azul e repetia, em faixa
              pintada, o que o título já gritava ao lado. Sem caixa e sem
              título ela é a primeira coisa da página e faz sozinha o trabalho
              de situar: um índice ALÉM do último passo acende a trilha inteira
              (ver TrilhaCompra), que é o estado de quem pagou; em
              processamento a pessoa ainda está parada em "Pagamento". */}
          <TrilhaCompra
            atual={pago ? ETAPAS_COMPRA.length : ETAPAS_COMPRA.length - 1}
            className="max-w-[720px] mx-auto my-8"
          />

          {/* O BLOCO DE ESTADO NO LUGAR DO TÍTULO.
              Saiu o <h1> "Compra concluída!" a pedido do dono, e com ele o
              único lugar da tela que dizia em voz alta o que tinha acontecido
              — que é exatamente o que a pessoa veio ver. O estado desceu para
              cá com peso de manchete: o selo (ou a ampulheta) à esquerda, a
              frase, a confirmação em cor de estado logo abaixo e o número do
              pedido à direita; a linha de baixo guarda o que já morava aqui.
              Continua sendo um <h1> no código porque página sem cabeçalho
              nenhum deixa o leitor de tela sem ponto de entrada: o que saiu
              foi a faixa de título, não a estrutura do documento. */}
          <div className="bloco p-5">
            <div className="flex items-center gap-3.5 flex-wrap">
              {pago ? (
                <SeloSucesso tamanho={44} className="shrink-0" />
              ) : (
                <span className="shrink-0 w-11 h-11 rounded-full bg-amber-400/15 flex items-center justify-center">
                  <i className="bi bi-hourglass-split text-xl text-amber-300" />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <h1 className="titulo-display mb-0 text-[2.1rem] md:text-[2.4rem]">
                  {pago ? "Pagamento confirmado" : "Pagamento em processamento"}
                </h1>
                <p className={"text-sm mt-0.5 " + (pago ? "text-emerald-300" : "text-amber-300")}>
                  {pago ? "Compra concluída" : "Aguardando confirmação"}
                </p>
              </div>
              <span className="shrink-0 text-white/40 text-sm">Nº {pedido.id}</span>
            </div>

            <div className="mt-3.5 pt-3.5 border-t border-line flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
              <span className="text-white/60">{pedido.livros?.length || 0} {pedido.livros?.length === 1 ? "item" : "itens"}</span>
              <span className="text-white/60">Total: <span className="text-white font-semibold">{fmtBRL((pedido.valorTotal || 0) / 100)}</span></span>
              <button onClick={irParaMinhasCompras} className="ml-auto text-insigne-orange font-medium hover:underline">Acompanhar Pedido</button>
            </div>
          </div>

          {/* Os dois quadros abaixo são AVISOS DE ESTADO, não cartões: raio de
              painel e borda de 2px na cor do estado. A espessura os deixa
              irmãos do `.cartao` do resto do site; a ausência da sombra dura os
              mantém como recado, e não como objeto que se pega. */}
          {pago ? (
            <div className="mt-5 rounded-painel border-2 border-emerald-400/40 bg-emerald-400/5 p-6">
              <p className="text-sm text-white/80">
                Seus e-books já estão liberados no seu perfil e enviamos um e-mail de confirmação.
                {pedido.frete_valor > 0 && " Os livros físicos entram em preparação para envio."}
              </p>
              {/* O presente aparece AQUI porque é o momento em que a leitura
                  passa a existir de verdade: até o pagamento confirmar, ela
                  não está liberada. Quem comprou o impresso e não for avisado
                  aqui vai esperar o carteiro sem saber que já pode ler hoje.
                  A condição é o item ser físico E carregar a flag — a mesma que
                  o servidor usa para liberar. */}
              {temBrinde && (
                <p className="mt-3 text-sm text-amber-200">
                  <i className="bi bi-gift-fill mr-1.5" />
                  O e-book do seu livro impresso já está na sua conta, em Meus Livros: leia
                  aqui no site enquanto o exemplar não chega. O arquivo em PDF não é enviado.
                </p>
              )}
              <button onClick={irParaMinhasCompras} className="mt-4 rounded-controle bg-insigne-orange text-paper px-5 py-2.5 text-sm font-semibold hover:bg-insigne-orange-dark">
                Ver meus livros
              </button>
            </div>
          ) : (
            <div className="mt-5 rounded-painel border-2 border-amber-400/40 bg-amber-400/5 p-6">
              <p className="text-sm text-white/80">
                Ainda não recebemos a confirmação do pagamento. Se você acabou de pagar, isso costuma
                levar alguns segundos. Clique em atualizar. Assim que confirmar, os livros aparecem
                no seu perfil e você recebe um e-mail.
              </p>
              <button onClick={consultar} className="mt-4 inline-flex items-center gap-2 rounded-controle bg-insigne-orange text-paper px-5 py-2.5 text-sm font-semibold hover:bg-insigne-orange-dark">
                <i className="bi bi-arrow-clockwise" /> Atualizar status
              </button>
            </div>
          )}

          {pedido.livros?.length > 0 && (
            <div className="mt-5 bloco p-5">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-white/45 mb-4">Resumo</h3>
              <div className="divide-y divide-line">
                {pedido.livros.map((item, i) => (
                  <div key={`${item.id}-${i}`} className="flex gap-4 py-4 first:pt-0 last:pb-0">
                    <div className="w-12 h-16 rounded-card overflow-hidden bg-insigne-navy-deep shrink-0 flex items-center justify-center">
                      {capaUrl(item) ? <img src={capaUrl(item)} alt={item.titulo} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} /> : <i className="bi bi-book text-white/30" />}
                    </div>
                    <div className="flex-1 min-w-0 flex justify-between gap-3">
                      <div className="min-w-0">
                        <h4 className="font-semibold text-sm leading-tight">{item.titulo || "Produto"}</h4>
                        <p className="text-white/45 text-xs mt-1">{item.formato === "fisico" ? "Livro físico" : "E-book"}{item.quantidade > 1 ? ` · ${item.quantidade}x` : ""}</p>
                      </div>
                      <p className="text-sm shrink-0"><span className="text-[10px] align-top">R$</span> <span className="font-bold">{fmtPreco(item.preco)}</span></p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* `-mx-6`, e não `-mx-4 md:-mx-8`: o respiro lateral da página é
              1,5rem em TODA largura, porque `px-4` é homônimo do Bootstrap e
              vence o `md:px-8` com !important. Com -2rem, este bloco passava 8px
              da borda direita e a página inteira rolava de lado a partir de
              768px (varredura de 18/09/2026). -1,5rem encosta exato. */}
          <div className="mt-6 -mx-6">
            <SliderProdutos nome="Compra também" icone="bi-bag-heart" />
          </div>
        </div>
      </div>
    );
  }

  // Acesso direto sem dados → volta para Minhas Compras.
  if (!state?.order) {
    return (
      <div className="bg-insigne-bg min-h-screen pt-[var(--nav-h)] px-4 text-white">
        <div className="bloco mx-auto my-12 max-w-[440px] p-8 text-center">
          <i className="bi bi-bag-check text-5xl text-white/30 mb-4 block" />
          <h2 className="titulo-display text-[2rem]">Nenhuma compra recente encontrada</h2>
          <button onClick={irParaMinhasCompras} className="mt-5 rounded-controle px-5 py-2.5 text-sm font-semibold bg-insigne-orange text-paper hover:bg-insigne-orange-dark">
            Ver minhas compras
          </button>
        </div>
      </div>
    );
  }

  // ===== Caminho 2: PIX da Insigne (pedido abaixo do mínimo da InfinitePay) =====
  const { order, qrCode, pix, total, livros = [], freteDias = 20 } = state;
  const numero = order?.id || "-";

  const copiarPix = () => {
    if (!qrCode) return;
    navigator.clipboard.writeText(qrCode);
    message.success("Código PIX copiado!");
  };

  const copiarChave = () => {
    if (!pix?.chave) return;
    navigator.clipboard.writeText(pix.chave);
    message.success("Chave PIX copiada!");
  };

  return (
    <div className="bg-insigne-bg fundo-abertura min-h-screen pt-[var(--nav-h)] px-4 md:px-8 pb-16 text-white">
      <div className="max-w-[1180px] mx-auto">
        {/* Este caminho não tinha trilha nenhuma, e era aqui que ela mais
            fazia falta: o pedido existe, mas o dinheiro não saiu do lugar.
            Parada em "Pagamento" (o último índice, e não um passo além dele),
            ela diz que ainda falta um passo — que fechar esta tela sem pagar
            não conclui a compra. */}
        <TrilhaCompra atual={ETAPAS_COMPRA.length - 1} className="max-w-[720px] mx-auto my-8" />

        {/* Mesmo bloco de estado do outro caminho, no lugar do antigo <h1>
            "Pedido criado!". O selo confirma o que JÁ deu certo (o pedido
            existe e está guardado) e a linha âmbar logo abaixo diz o que
            ainda falta, sem que uma coisa apague a outra. */}
        <div className="bloco p-5">
          <div className="flex items-center gap-3.5 flex-wrap">
            <SeloSucesso tamanho={44} className="shrink-0" />
            <div className="min-w-0 flex-1">
              <h1 className="titulo-display mb-0 text-[2.1rem] md:text-[2.4rem]">Pedido criado</h1>
              <p className="text-amber-300 text-sm mt-0.5">Aguardando pagamento do PIX</p>
            </div>
            <span className="shrink-0 text-white/40 text-sm">Nº {String(numero).slice(0, 12)}</span>
          </div>

          <div className="mt-3.5 pt-3.5 border-t border-line flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
            <span className="text-white/60">{livros.length} {livros.length === 1 ? "item" : "itens"}</span>
            <span className="text-white/60">Total: <span className="text-white font-semibold">{fmtBRL(total)}</span></span>
            <button onClick={irParaMinhasCompras} className="ml-auto text-insigne-orange font-medium hover:underline">Acompanhar Pedido</button>
          </div>
        </div>

        {qrCode && (
          <div className="mt-5 bloco p-6">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-white/45 mb-4">Pague com PIX para concluir</h3>
            <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
              {/* A ênfase é a sombra sólida da casa, e ela fica no CARTÃO - o
                  QR em si não recebe filtro, borda nem sombra, sob pena de a
                  câmera do celular não conseguir ler. Degrau pequeno e parado:
                  não é objeto para levar o cursor, é para apontar o celular. */}
              <div className="bg-paper p-3 rounded-card shrink-0 shadow-[3px_3px_0_0_rgb(var(--c-sombra))]">
                <QRCodeCanvas value={qrCode} size={200} level="M" />
              </div>
              <div className="flex-1 min-w-0 w-full">
                <p className="text-sm text-white/70 mb-3">
                  Abra o app do seu banco, escaneie o QR Code (ou use o Pix Copia e Cola abaixo) e
                  confirme o pagamento. O valor já vem preenchido.
                </p>

                {/* POÇO, e não cartão: rebaixo dentro do bloco do QR. Continua
                    na borda de 1px e sem sombra — dar relevo a um poço inverte
                    o que ele diz. O raio é o de card, como o dos outros poços
                    do site, e não o de painel: o poço mora DENTRO do bloco, e um
                    canto de dentro tão redondo quanto o de fora apaga a diferença
                    entre o que contém e o que é contido. */}
                {pix && (
                  <div className="rounded-card border border-line bg-insigne-navy-deep p-4 mb-3 text-sm space-y-1.5">
                    <div className="flex justify-between gap-3">
                      <span className="text-white/50">Beneficiário</span>
                      <span className="text-white font-medium text-right">{pix.beneficiario}</span>
                    </div>
                    <div className="flex justify-between gap-3 items-center">
                      <span className="text-white/50">Chave PIX ({pix.tipo_chave})</span>
                      <button onClick={copiarChave} className="text-white font-medium hover:text-insigne-orange transition-colors text-right">
                        {pix.chave} <i className="bi bi-clipboard text-xs" />
                      </button>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-white/50">Valor</span>
                      <span className="text-white font-bold">{fmtBRL(pix.valor ?? total)}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-white/50">Identificador</span>
                      <span className="text-white/80">{pix.txid}</span>
                    </div>
                  </div>
                )}

                <label className="block text-xs text-white/45 mb-1">Pix Copia e Cola</label>
                <textarea readOnly value={qrCode} rows={4} className="w-full rounded-controle bg-insigne-navy-deep border border-line px-3 py-2 text-xs text-white/80 resize-none" />
                <button onClick={copiarPix} className="mt-2 inline-flex items-center gap-2 rounded-controle bg-insigne-orange text-paper px-4 py-2.5 text-sm font-semibold hover:bg-insigne-orange-dark">
                  <i className="bi bi-clipboard" /> Copiar Código PIX
                </button>

                <p className="text-xs text-amber-300 mt-3">
                  A confirmação é feita pela equipe da Insigne após a conferência do PIX. Você
                  receberá um e-mail e os livros aparecem em "Minhas Compras".
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-5 bloco p-5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-white/45 mb-4">Resumo</h3>
          <div className="divide-y divide-line">
            {livros.map((item) => (
              <div key={`${item.id}-${item.formato}`} className="flex gap-4 py-4 first:pt-0 last:pb-0">
                <div className="w-12 h-16 rounded-card overflow-hidden bg-insigne-navy-deep shrink-0 flex items-center justify-center">
                  {capaUrl(item) ? <img src={capaUrl(item)} alt={item.titulo} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} /> : <i className="bi bi-book text-white/30" />}
                </div>
                <div className="flex-1 min-w-0 flex justify-between gap-3">
                  <div className="min-w-0">
                    <h4 className="font-semibold text-sm leading-tight">{item.titulo || "Produto"}</h4>
                    {autorDe(item) && <p className="text-white/55 text-xs mt-0.5">{autorDe(item)}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm"><span className="text-[10px] align-top">R$</span> <span className="font-bold">{fmtPreco(item.preco)}</span></p>
                    <p className="text-white/45 text-xs mt-1">Previsão de Entrega:<br />{item.formato === "fisico" ? previsaoEntrega(new Date(), freteDias) : "Imediata"}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* `-mx-6`, e não `-mx-4 md:-mx-8`: o respiro lateral da página é
            1,5rem em TODA largura, porque `px-4` é homônimo do Bootstrap e
            vence o `md:px-8` com !important. Com -2rem, este bloco passava 8px
            da borda direita e a página inteira rolava de lado a partir de
            768px (varredura de 18/09/2026). -1,5rem encosta exato. */}
        <div className="mt-6 -mx-6">
          <SliderProdutos nome="Compra também" icone="bi-bag-heart" />
        </div>
      </div>
    </div>
  );
};

export default CompraConcluida;
